import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Link, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { subscribeGlobalMessages } from '../../utils/globalMessageEvents';

const DRAWER_WIDTH_DEFAULT = 280;
const BANNER_TOP_PX = 92;
/** Collapse identical errors within this window so a flood of 502s shows once. */
const DEDUPE_WINDOW_MS = 8000;

function buildTicketDraft(detail, user) {
  const subject =
    detail.message.length > 120 ? `${detail.message.slice(0, 117)}…` : detail.message;
  const lines = [
    '— Auto-filled from application error —',
    '',
    `Message: ${detail.message}`,
    `When (UTC): ${detail.occurredAt || ''}`,
    `Screen path: ${detail.path || ''}`,
    `Full URL: ${detail.fullUrl || ''}`,
  ];
  if (detail.httpMethod || detail.requestUrl || detail.httpStatus != null) {
    lines.push(
      `Request: ${detail.httpMethod || ''} ${detail.requestUrl || ''}`.trim() +
        (detail.httpStatus != null ? ` → HTTP ${detail.httpStatus}` : '')
    );
  }
  if (user?.email) lines.push(`User: ${user.email}`);
  if (user?.id != null) lines.push(`User id: ${user.id}`);
  if (detail.source) lines.push(`Source: ${detail.source}`);
  if (detail.stack) {
    lines.push('', 'Stack:', String(detail.stack).slice(0, 8000));
  }
  if (detail.componentStack) {
    lines.push('', 'Component stack:', String(detail.componentStack).slice(0, 4000));
  }
  lines.push('', 'Steps to reproduce (add below):', '');
  const description = lines.filter(Boolean).join('\n');
  return { subject: `[App error] ${subject}`, description };
}

export default function GlobalMessageBanner({ drawerWidth = DRAWER_WIDTH_DEFAULT }) {
  const navigate = useNavigate();
  const { user, hasModuleAccess } = useAuth();
  const [items, setItems] = useState([]);

  const canHelpdesk = useMemo(() => hasModuleAccess('helpdesk'), [hasModuleAccess]);

  useEffect(() => {
    return subscribeGlobalMessages((detail) => {
      if (detail.kind !== 'error' && detail.kind !== 'info') return;
      const path = typeof window !== 'undefined' ? window.location.pathname : '';
      const fullUrl = typeof window !== 'undefined' ? window.location.href : '';
      const msgKey = `${detail.kind}|${String(detail.message || '').trim()}|${detail.httpStatus || ''}`;
      const now = Date.now();
      setItems((prev) => {
        const recent = prev.find(
          (x) =>
            `${x.kind}|${String(x.message || '').trim()}|${x.httpStatus || ''}` === msgKey &&
            now - new Date(x.occurredAt || 0).getTime() < DEDUPE_WINDOW_MS
        );
        if (recent) return prev;
        return [
          ...prev.slice(-9),
          {
            ...detail,
            path: detail.path || path,
            fullUrl: detail.fullUrl || fullUrl,
          },
        ];
      });
    });
  }, []);

  const dismiss = useCallback((id) => {
    setItems((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const openTicket = useCallback(
    (detail) => {
      const draft = buildTicketDraft(detail, user);
      navigate('/helpdesk/new', { state: { ticketDraft: draft } });
    },
    [navigate, user]
  );

  if (items.length === 0) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        zIndex: (t) => t.zIndex.appBar - 1,
        top: BANNER_TOP_PX,
        left: { xs: 0, md: `${drawerWidth}px` },
        width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
        px: { xs: 1, sm: 2 },
        pointerEvents: 'none',
        '& > *': { pointerEvents: 'auto' },
      }}
    >
      <Stack spacing={1}>
        {items.map((row) => {
          const isErr = row.kind === 'error';
          return (
            <Box
              key={row.id}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                py: 1,
                px: 1.5,
                borderRadius: 1,
                border: '1px solid',
                boxShadow: 1,
                bgcolor: isErr ? 'rgba(254, 226, 226, 0.98)' : 'rgba(220, 252, 231, 0.98)',
                borderColor: isErr ? 'rgba(248, 113, 113, 0.6)' : 'rgba(74, 222, 128, 0.5)',
                color: isErr ? '#7f1d1d' : '#14532d',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  wordBreak: 'break-word',
                  fontFamily: isErr ? 'inherit' : 'inherit',
                }}
              >
                {row.message}
              </Typography>
              {isErr && canHelpdesk ? (
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => openTicket(row)}
                  sx={{
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                    fontWeight: 600,
                    color: 'inherit',
                    textDecoration: 'underline',
                  }}
                >
                  Log a ticket
                </Link>
              ) : null}
              <IconButton
                size="small"
                aria-label="Dismiss message"
                onClick={() => dismiss(row.id)}
                sx={{ flexShrink: 0, color: 'inherit', mt: -0.25 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
